<template>
  <div class="date_range_picker" @click.stop>
    <div v-if="timezone" class="date_picker_timezone">
      <span>{{ $t('common.timezone') }}</span>
      <span class="value">{{timezoneName}}</span>
    </div>
    <div class="date_range_picker--wrap">
      <div class="date_range_picker_label--wrap now-date"
           :class="{ active: selected === 'now' }">
        <span class="date_range_picker_label">
          {{ $t('journey.calendar.analysis') }}
        </span>
        <div class="date_range_picker_input--wrap">
          <span class="date_range_picker_input">
            <i class="icon icon--calendar-input"></i><input type="text" :value="startDateStr" readonly></span>
          <span> ~ </span>
          <span class="date_range_picker_input">
            <i class="icon icon--calendar-input"></i><input type="text" :value="endDateStr" readonly></span>
        </div>
      </div>
    </div>
    <transition name="info-for-before-2022">
      <p v-if="isStartDateBefore2022"
         class="info-for-before-2022">
        <i class="icon icon--ring"></i>
        {{ $t('journey.calendar.before-2022') }}
      </p>
      <p v-if="isOnlyShinhanSampleDate"
         class="info-for-before-2022">
        <i class="icon icon--ring"></i>
        {{ $t('journey.calendar.onlyShinhanReSampleLimit', {planName: 'Journey 500MS', startDate:shinhanResampleStartDate}) }}
      </p>
    </transition>
    <div class="calendar clearfix">
      <month-calendar v-for="index in 2" :key="index"
                      :class="index === 1 ? 'left' : 'right'"
                      :uiType="index === 1 ? 'left' : 'right'"
                      :monthStr="tempMonthStr"
                      :minDate="computedMinDate"
                      :maxDate="computedMaxDate"
                      :minSelectableDate="minSelectableDate"
                      :dateRange="tempDateRange"
                      :previewEndDate="previewEndDate"
                      :showPaid="showPaid"
                      :highlight="showPaid ? planData.paidRange : null"
                      :unpaid="showPaid ? planData.unpaidRange : null"
                      @onMonthAdd="onMonthAdd"
                      @onDateClick="onDateClick"
                      @onRangePreviewChange="onRangePreviewChange"/>
    </div>
    <div class="clearfix">
      <div class="plan-legend">
        <span v-if="planData.unpaidRange.length !== 0" class="legend everyone" v-html="$t('journey.calendar.legend.everyone')"></span>
        <span v-if="planData.paidRange.length !== 0" class="legend pro" v-html="$t('journey.calendar.legend.pro')"></span>
      </div>
    </div>
    <div class="clearfix">
      <p v-if="pickerType === 'journeyPeriod'" class="notice">
        <span v-html="$t('journey.calendar.availablePeriod')"></span>
      </p>
      <p class="action">
        <ui-button @click.stop="onCancelClick">
          {{ $t('common.cancel') }}
        </ui-button>
        <ui-button primary
          :disabled="!canApply"
          @click.stop="onConfirmClick">
          {{ $t('common.apply') }}
        </ui-button>
      </p>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { format, parse, addMonths, isBefore, isAfter, addDays } from 'date-fns'
import { getLocaleDateStr, getOffset } from '@/libs/utils'
import MonthCalendar from '@/components/ui/ba/MonthCalendar'
import UiButton from '@/components/ui/UiButton'
import { SHINHAN_RESAMPLE_START_DATE, SHINHAN_SAMPLE_END_DATE, K_CAR_SAMPLING_START_DATE, SAMSUNG_SAMPLING_START_DATE } from '@/libs/constants'

export default {
  name: 'DateRangePicker',
  components: {
    MonthCalendar,
    UiButton
  },
  props: {
    pickerType: {
      type: String,
      default: 'journeyPeriod'
    },
    selected: {
      type: String,
      default: 'now'
    },
    timezoneName: {
      type: String,
      required: false
    },
    minDate: {
      type: Date,
      required: false
    },
    minSelectableDate: {
      type: Date,
      required: false
    },
    maxDate: {
      type: Date,
      required: false
    },
    dateRange: {
      type: Object,
      default () {
        return {
          startDate: null,
          endDate: null
        }
      }
    },
    monthStr: {
      type: String,
      required: false
    },
    maxDays: {
      type: Number,
      default: 0
    },
    showPaid: {
      type: Boolean,
      default: false
    },
    excludeTodayBefore: {
      default: false
    }
  },
  data () {
    return {
      tempMonthStr: null,
      tempDateRange: null,
      previewEndDate: null,
      isStartDateBefore2022: false,
      isOnlyShinhanSampleDate: false
    }
  },
  computed: {
    ...mapState([
      'userData',
      'planData',
      'domain',
      'isSampling'
    ]),
    shinhanResampleStartDate () {
      return SHINHAN_RESAMPLE_START_DATE
    },
    timezone () {
      return getOffset(this.timezoneName)
    },
    startDateStr () {
      if (this.tempDateRange.startDate) {
        return getLocaleDateStr(this.tempDateRange.startDate)
      } else {
        return this.$t('common.placeHolder.startDate')
      }
    },
    endDateStr () {
      if (this.tempDateRange.endDate) {
        return getLocaleDateStr(this.tempDateRange.endDate)
      } else {
        return this.$t('common.placeHolder.endDate')
      }
    },
    canApply () {
      return this.tempDateRange.startDate && this.tempDateRange.endDate
    },
    computedMinDate () {
      let newMinDate = this.minDate

      // Samsung sampling - Date range restriction
      if (this.domain.sid === 'sf7f94f69d5' && (this.userData.userId === 'samsung_gmc' || this.userData.isSamsung)) {
        const samplingStartDate = new Date(SAMSUNG_SAMPLING_START_DATE)
        newMinDate = isAfter(samplingStartDate, newMinDate) ? samplingStartDate : newMinDate
      }

      return newMinDate
    },
    computedMaxDate () {
      let newMaxDate = this.maxDate

      if (this.maxDays && this.tempDateRange.startDate && !this.tempDateRange.endDate) {
        if (this.isStartDateBefore2022) {
          return new Date('2021-12-31')
        }

        if (this.excludeTodayBefore) {
          const today = new Date()
          const yesterday = addDays(today, -1)
          const maginotDate = addDays(today, -this.excludeTodayBefore)

          if (isBefore(yesterday, newMaxDate) && isBefore(this.tempDateRange.startDate, maginotDate)) {
            newMaxDate = yesterday
          }
        }

        const newEndDate = addDays(this.tempDateRange.startDate, this.maxDays - 1)

        if (!newMaxDate || isBefore(newEndDate, newMaxDate)) {
          newMaxDate = newEndDate
        }

        // temp resample shinhan_test
        if (this.domain.userId === 'shinhan_test' && this.tempDateRange.startDate < new Date(SHINHAN_RESAMPLE_START_DATE + 'T00:00:00.000')) {
          const samplingEndDate = new Date(SHINHAN_SAMPLE_END_DATE)
          newMaxDate = isBefore(samplingEndDate, newMaxDate) ? samplingEndDate : newMaxDate
        }

        // Kcar sampling - Date range restriction
        if (this.domain.sid === 'sb58f597b81' && this.tempDateRange.startDate < new Date(K_CAR_SAMPLING_START_DATE + 'T00:00:00.000')) {
          const samplingStartDate = new Date(K_CAR_SAMPLING_START_DATE)
          const dayBeforeSamplingStart = new Date(samplingStartDate.setDate(samplingStartDate.getDate() - 1))
          newMaxDate = isBefore(dayBeforeSamplingStart, newMaxDate) ? dayBeforeSamplingStart : newMaxDate
        }
      }

      return newMaxDate
    }
  },
  created () {
    if (this.monthStr) {
      this.tempMonthStr = this.monthStr
    } else {
      const range = this.dateRange

      if (range && range.startDate && range.endDate) {
        this.tempMonthStr = format(range.endDate, 'YYYY-MM')
      } else {
        this.tempMonthStr = format(new Date(), 'YYYY-MM')
      }
    }
    this.setTempDateRange()

    this.closeFn = () => {
      this.onCancelClick()
    }
    document.body.addEventListener('click', this.closeFn, false)
  },
  destroyed () {
    document.body.removeEventListener('click', this.closeFn)
  },
  methods: {
    setTempDateRange () {
      this.tempDateRange = { ...this.dateRange }
    },
    onMonthAdd (months) {
      let monthDate = parse(this.tempMonthStr)
      monthDate = addMonths(monthDate, months)
      this.tempMonthStr = format(monthDate, 'YYYY-MM-DD')
    },
    onRangePreviewChange (type, date) {
      this.previewEndDate = date
    },
    onDateClick (date, type) {
      const range = this.tempDateRange

      this.isStartDateBefore2022 = false
      if (!range.startDate || range.endDate || (!range.endDate && isBefore(date, range.startDate))) {
        range.startDate = date
        range.endDate = null
        this.previewEndDate = null

        if (range.startDate.getFullYear() < 2022) {
          this.isStartDateBefore2022 = true
        }

        if (this.domain.userId === 'shinhan_test' && range.startDate < new Date(SHINHAN_RESAMPLE_START_DATE + 'T00:00:00.000')) {
          this.isOnlyShinhanSampleDate = true
        } else {
          this.isOnlyShinhanSampleDate = false
        }
      } else {
        range.endDate = date
        this.previewEndDate = null
      }
    },
    onCancelClick () {
      this.$emit('onRangeCancel')
    },
    onConfirmClick () {
      const range = this.tempDateRange

      if (!range.startDate || !range.endDate) {
        return
      }
      this.$emit('onRangeChange', {
        range: this.tempDateRange,
        monthStr: this.tempMonthStr
      })
    }
  }
}
</script>

<style lang="scss" scoped="scoped">
.date_range_picker {
  padding: 30px 27px 25px 27px;

  .month_calendar {
    margin: -5px -5px -10px 0;
  }
  .calendar {
    display: flex;
    gap: 25px;
  }
  .date_picker_timezone {
    margin-bottom: 20px;

    span {
      display: inline-block;
      vertical-align: middle;
    }

    .icon {
      vertical-align: middle;
    }
  }

  .date_range_picker--wrap {
    border-bottom: 1px dashed #d7d7d7;
    padding-bottom: 8px;

    .date_range_picker_label--wrap {
      border: 2px solid transparent;
      padding: 10px 0;
      margin-top: 5px;
      text-align: start;

      .date_range_picker_label {
        display: inline-block;
        line-height: 38px;
        padding: 0 5px;
        font-size: 14px;
        font-weight: 500;

        .icon {
          vertical-align: middle;
          margin-right: 10px;
        }
      }

      .date_range_picker_input--wrap {
        float: right;
        width: 346px;

        .date_range_picker_input {
          input {
            width: 160px;
          }
        }
      }
    }
  }

  .plan-legend {
    border-bottom: 1px dashed #d7d7d7;
    padding: 26px 0 20px;
    text-align: center;
    margin-bottom: 3px;

    .legend {
      font-size: 12px;
      color: #888888;
      margin: 0 13px;

      &::before {
        content: "";
        display: inline-block;
        vertical-align: middle;
        width: 8px;
        height: 8px;
        background-color: #ddd;
        border-radius: 5px;
        margin: 0 7px 2px 0;
      }

      &.everyone {
        &::before {
          background-color: #333333;
        }

        & /deep/ span {
          color: #333333;
        }
      }

      &.pro {
        &::before {
          background-color: #FF2D92;
        }

        & /deep/ span {
          color: #FF2D92;
        }
      }
    }
  }
  // height: 16px;
  margin: 2px 0 2px 8px;
  font-size: 12px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.33;
  letter-spacing: normal;
  color: #444;

  .info-for-before-2022 {
    position: relative;
    height: 28px;
    font-size: 12px;
    color: #444;
    line-height: 30px;
    margin: 4px 0 19px;
    padding: 0 12px 0 32px;
    border-radius: 5px;
    background-color: #f4e439;
    overflow: hidden;

    &-leave-active,
    &-enter-active {
      height: 28px;
      margin: 4px 0 19px;
      opacity: 1;
      transition: 0.25s ease;
      transition-property: height, margin, opacity;
    }

    &-enter,
    &-leave-to {
      height: 0;
      margin: 0;
      opacity: 0;
    }

    .icon {
      position: absolute;
      left: 4px;
      top: 4px;
    }
  }
}
</style>
